import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import { ExchangeRates, ExchangeRateProps, ExchangeRateState } from './Interface';
import DataGrid from '../../Component/DataGrid/DataGrid';
import '../UserDetails/UserDetails.css';
import { formatLimit, convertToNumber } from '../../Component/config';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import AddCurrencyPairs from './AddCurrencyPairs';
import ExchanageRateService from '../../Services/ExchnageRateService';
import UpdateCurrencyPairs from './EditCurrencyPairRates';
import { ShowAlert } from '../../Component/ShowAlert';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import { Status } from '../../Component/Reusables/Utility';

class ExchangeRate extends React.Component<ExchangeRateProps, ExchangeRateState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private tableColumns: TableColumn<ExchangeRates>[] = [
        {
            name: 'Currency ID',
            selector: row => row.currencyId,
            sortable: true,
            wrap: true,
            minWidth: "250px",
            omit: true
        },
        {
            name: 'From Country',
            selector: row => `${row.senderCountry} (${row.fromCurrency})`,
            sortable: true,
            id: "fromCurrency",
            wrap: true,
            minWidth: "200px"
        },
        {
            name: 'To Country',
            selector: row => `${row.receiverCountry} (${row.toCurrency})`,
            sortable: true,
            id: "toCurrency",
            wrap: true,
            minWidth: "200px"
        },
        {
            name: 'Exchange Rate',
            selector: row => formatLimit(row.exchangeRate, "en-US", "USD", row.receiverCurrencySymbol),
            sortable: true,
            id: "exchangeRate",
            right: true,
            minWidth: "150px"
        },
        {
            name: 'Markup %',
            selector: row => row.markupPercentage != null ? `${row.markupPercentage}%` : "",
            sortable: true,
            id: "markupPercentage",
            wrap: true,
            right: true,
            minWidth: "120px"
        },
        {
            name: 'Platform Fee %',
            selector: row => row.platformFeePercentage != null ? `${row.platformFeePercentage}%` : "",
            sortable: true,
            id: "platformFeePercentage",
            wrap: true,
            right: true,
            minWidth: "140px"
        },
        {
            name: 'Platform Fee',
            selector: row => formatLimit(row.platformFee, "en-US", "USD", "$"),
            sortable: true,
            id: "platformFee",
            wrap: true,
            right: true,
            minWidth: "120px"
        },
        {
            name: 'Status',
            selector: row => row.isActive ? Status.ACTIVE : Status.INACTIVE,
            sortable: true,
            id: "status",
            cell: (row) => {
                const statusLabel = row.isActive ? <span className='status-label active'>{Status.ACTIVE}</span> :
                    <span className="status-label blocked">{Status.INACTIVE}</span>
                return statusLabel;
            },
            minWidth: "120px",
            center: true
        },
        {
            name: 'Actions',
            cell: row => {
                const { exchangeRates } = this.state;

                const popoverItems = [];

                // Conditionally add "Edit Rate/Fees"
                if (exchangeRates.isEditRatesVisible) {
                    popoverItems.push({ key: "edit_currencies", label: "Edit Rate/Fees" });
                }

                // Conditionally add "Active"/"Inactive" toggle
                if (row.isActive && exchangeRates.isInactiveCurrencyAllowed) {
                    popoverItems.push({ key: "Inactive", label: "Inactive" });
                } else if (!row.isActive && exchangeRates.isActiveCurrencyAllowed) {
                    popoverItems.push({ key: "Active", label: "Active" });
                }

                // If no permitted actions, render nothing
                if (popoverItems.length === 0) {
                    return null;
                }

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={popoverItems}
                        onPopoverClick={(key_: string) => {
                            if (key_ === "Inactive" || key_ === "Active") {
                                const statusCode = key_ === "Active" ? 1 : 0;
                                this.handleStatusUpdate(row, statusCode);
                            } else if (key_ === "edit_currencies") {
                                this.setState({
                                    isOpenUpdateCurrencyRate: true,
                                    selectedRow: row
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '200px'
        }
    ];

    constructor(props_: ExchangeRateProps) {
        super(props_);

        this.state = {
            accessToken: "",
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: "",
            selectedFilter: "",
            filterValue: "",
            exchangeRate: [],
            selectedRow: null,
            isOpenAddCurrencyPairDialog: false,
            isOpenUpdateCurrencyRate: false,
            exchangeRates: {
                isVisible: true,
                isDownloadVisible: true,
                isAddCurrencyPairVisible: true,
                isEditRatesVisible: true,
                isActiveCurrencyAllowed: true,
                isInactiveCurrencyAllowed: true,
            },
        }
        this.getExchangeRate = this.getExchangeRate.bind(this);
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.exchangeRates?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const exchangeRates = permissions.exchangeRates

                        this.setState({ exchangeRates }, () => {
                            this.getExchangeRate();
                        });
                    } else {
                        this.getExchangeRate();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    private getExchangeRate() {
        let pageIndex: number = (this.state.currentPage - 1);
        let pagination: any = {
            "pageNumber": pageIndex,
            "recordPerPage": this.state.currentRowsPerPage
        }

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText
        }

        if (this.state.selectedFilter) {
            pagination[this.state.selectedFilter] = this.state.filterValue;
        }

        ExchanageRateService.getExchangeRate(this.state.accessToken, pagination).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    console.log(resp)
                    this.setState({
                        exchangeRate: resp.currencyPairList,
                        totalRows: resp.totalPages
                    })
                });
            }
        });
    }

    // Update the status of a currency pair: Activate or Block
    private async handleStatusUpdate(row_: ExchangeRates, status: number) {
        try {
            const postData = {
                currencyId: row_.currencyId,
                fromCurrency: row_.fromCurrency,
                toCurrency: row_.toCurrency,
                currencyPairStatus: status
            };

            const statusLabel = status === 1 ? "activated" : "blocked";

            const resp = await ExchanageRateService.updateCurrencyPairStatus(this.state.accessToken, postData);

            if (resp?.ok) {
                ShowAlert("PassportPay", `Currency pair has been successfully ${statusLabel}.`, "success")
                    .then(() => { this.getExchangeRate(); });
            }
        } catch (error) {
            console.error("Error updating currency pair status:", error);
            ShowAlert("Error", "An error occurred while updating the currency pair status. Please try again.", "error");
        }
    }

    public render() {
        const {
            isDownloadVisible,
            isAddCurrencyPairVisible,
        } = this.state.exchangeRates;
        return (
            <>
                <div className="body-sub-container table-container">
                    <DataGrid
                        title='Exchange Rates'
                        search='Currency'
                        onFilterChange={(selectedFilter: string, filterValue: string) => {
                            this.setState({
                                selectedFilter,
                                filterValue,
                                currentPage: 1,
                            }, () => this.getExchangeRate());
                        }}
                        defaultSearch={this.state.searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                currentPage: 1
                            }, () => {
                                this.getExchangeRate();
                            });
                        }}
                        columns={this.tableColumns}
                        data={this.state.exchangeRate}
                        onChangePage={(page: number, totalRows: number) => {
                            this.setState({
                                currentPage: page
                            }, () => {
                                this.getExchangeRate();
                            })
                        }}
                        onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                            this.setState({
                                currentRowsPerPage: currentRowsPerPage_,
                                currentPage: currentPage_
                            }, () => {
                                this.getExchangeRate();
                            })
                        }}
                        pagination
                        paginationTotalRows={this.state.totalRows}
                        //exportCsv={isDownloadVisible ? true : false}
                        addCurrencyPairs={isAddCurrencyPairVisible}
                        isOpenAddNewSupportStaffDialog={() => {
                            this.setState({ isOpenAddCurrencyPairDialog: true })
                        }}

                    />
                </div>
                {this.state.isOpenAddCurrencyPairDialog && (
                    <AddCurrencyPairs
                        open={this.state.isOpenAddCurrencyPairDialog}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenAddCurrencyPairDialog: false
                            })
                        }}
                        refreshData={this.getExchangeRate}
                        accessToken={this.state.accessToken}
                    />
                )}
                {this.state.isOpenUpdateCurrencyRate && this.state.selectedRow && (
                    <UpdateCurrencyPairs
                        open={this.state.isOpenUpdateCurrencyRate}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenUpdateCurrencyRate: false
                            })
                        }}
                        refreshData={this.getExchangeRate}
                        currencyPairData={this.state.selectedRow}
                        accessToken={this.state.accessToken}
                    />
                )}
            </>
        )
    }
}

export default ExchangeRate;